
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLocation as useLocationCtx } from '../context/LocationContext';
import { createRestaurantSlug } from '../context/LocationContext';
import { User, Package, MapPin, Heart, HelpCircle, LogOut, ShoppingCart, ChevronDown, Search, X } from 'lucide-react';
import LoginModal from './LoginModal';
import apiService from '../services/apiService';

const US_STATES = {
  AL:'Alabama', AK:'Alaska', AZ:'Arizona', AR:'Arkansas', CA:'California',
  CO:'Colorado', CT:'Connecticut', DE:'Delaware', FL:'Florida', GA:'Georgia',
  HI:'Hawaii', ID:'Idaho', IL:'Illinois', IN:'Indiana', IA:'Iowa',
  KS:'Kansas', KY:'Kentucky', LA:'Louisiana', ME:'Maine', MD:'Maryland',
  MA:'Massachusetts', MI:'Michigan', MN:'Minnesota', MS:'Mississippi', MO:'Missouri',
  MT:'Montana', NE:'Nebraska', NV:'Nevada', NH:'New Hampshire', NJ:'New Jersey',
  NM:'New Mexico', NY:'New York', NC:'North Carolina', ND:'North Dakota', OH:'Ohio',
  OK:'Oklahoma', OR:'Oregon', PA:'Pennsylvania', RI:'Rhode Island', SC:'South Carolina',
  SD:'South Dakota', TN:'Tennessee', TX:'Texas', UT:'Utah', VT:'Vermont',
  VA:'Virginia', WA:'Washington', WV:'West Virginia', WI:'Wisconsin', WY:'Wyoming',
  DC:'Washington DC',
};
const expandState = (s) => { if (!s) return ''; const u = s.trim().toUpperCase(); return US_STATES[u] || s; };

const Navbar = () => {
  const { isAuthenticated, customer, logout } = useAuth();
  const { cartItemCount, cartRestaurant, cartItems } = useCart();
  const { selectedLocation, setSelectedLocation, getCurrentLocation, reverseGeocode, geocodeAddress } = useLocationCtx();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [navSearchQuery, setNavSearchQuery] = useState('');
  const [kitchenSuggestions, setKitchenSuggestions] = useState([]);
  const [dishSuggestions, setDishSuggestions] = useState([]);
  const [locationSearchSuggestions, setLocationSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimerRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    setProfileOpen(false);
    navigate('/customer');
  };

  const handleCartClick = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
    } else if (cartRestaurant && cartItems.length > 0) {
      // Navigate to the kitchen page that has items in cart (75/25 layout)
      const kitchenId = cartRestaurant.id;
      const kitchenName = cartRestaurant.name;
      const kitchenSlug = createRestaurantSlug(kitchenName) || kitchenId;
      if (selectedLocation?.slug) {
        navigate(`/customer/${selectedLocation.slug}/${kitchenSlug}`);
      } else {
        navigate(`/customer/kitchen/${kitchenId}`);
      }
    }
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  // Unified Search with Kitchen + Dish + Location suggestions
  const handleNavSearch = async (q) => {
    setNavSearchQuery(q);
    
    if (q.trim().length < 2) {
      setKitchenSuggestions([]);
      setDishSuggestions([]);
      setLocationSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce the search
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const query = q.toLowerCase();
        
        // Fetch all kitchens — pass the search term to backend so it handles state name expansion too
        const params = { limit: 100, search: q };
        const kitchensRes = await apiService.getKitchens(params);
        const allKitchens = kitchensRes?.data?.kitchens || kitchensRes?.data || [];
        
        // Also filter client-side (covers cases backend might miss, e.g. full state name)
        const matchingKitchens = allKitchens.filter(k => {
          const name      = (k.kitchen_name || k.name || '').toLowerCase();
          const cuisine   = (k.cuisine_type || '').toLowerCase();
          const address   = (k.address_street || '').toLowerCase();
          const city      = (k.address_city || '').toLowerCase();
          const stateAbbr = (k.address_state || '').toLowerCase();
          const stateFull = expandState(k.address_state).toLowerCase();
          const zip       = (k.address_zip || '').toLowerCase();
          return name.includes(query) || cuisine.includes(query) ||
                 address.includes(query) || city.includes(query) ||
                 stateAbbr.includes(query) || stateFull.includes(query) ||
                 zip.includes(query);
        }).slice(0, 5);
        setKitchenSuggestions(matchingKitchens);

        // Fetch dishes from matching kitchens first, then others
        const dishMatches = [];
        for (const kitchen of allKitchens.slice(0, 20)) {
          const kid = kitchen.kitchen_id || kitchen.id;
          try {
            const menuRes = await apiService.getKitchenMenu(kid);
            const menuItems = menuRes?.data || [];
            let flatItems = [];
            if (Array.isArray(menuItems)) {
              if (menuItems.length > 0 && menuItems[0]?.items) {
                menuItems.forEach(group =>
                  (group.items || []).forEach(item =>
                    flatItems.push({ ...item, kitchenId: kid, kitchenName: kitchen.kitchen_name || kitchen.name })
                  )
                );
              } else {
                flatItems = menuItems.map(item => ({ ...item, kitchenId: kid, kitchenName: kitchen.kitchen_name || kitchen.name }));
              }
            }
            const matchingDishes = flatItems.filter(item => {
              const itemName    = (item.item_name || item.name || '').toLowerCase();
              const description = (item.description || '').toLowerCase();
              const category    = (item.category || '').toLowerCase();
              return itemName.includes(query) || description.includes(query) || category.includes(query);
            });
            dishMatches.push(...matchingDishes);
            if (dishMatches.length >= 5) break;
          } catch (e) { /* skip */ }
        }
        setDishSuggestions(dishMatches.slice(0, 5));

        // Geocode the query to get location suggestions (run in parallel — don't block)
        geocodeAddress(q).then(places => {
          setLocationSearchSuggestions((places || []).slice(0, 3));
        }).catch(() => setLocationSearchSuggestions([]));

        setShowSuggestions(true);
      } catch (error) {
        console.error('Search error:', error);
        setKitchenSuggestions([]);
        setDishSuggestions([]);
        setLocationSearchSuggestions([]);
      }
    }, 350);
  };

  const handleKitchenClick = (kitchen) => {
    const kid = kitchen.kitchen_id || kitchen.id;
    const kitchenSlug = createRestaurantSlug(kitchen.kitchen_name || kitchen.name) || kid;
    setShowSuggestions(false);
    setNavSearchQuery('');
    if (selectedLocation?.slug) {
      navigate(`/customer/${selectedLocation.slug}/${kitchenSlug}`);
    } else {
      navigate(`/customer/kitchen/${kid}`);
    }
  };

  const handleDishClick = (dish) => {
    const kid = dish.kitchenId;
    const kitchen = kitchenSuggestions.find(k => (k.kitchen_id || k.id) === kid);
    const kitchenName = dish.kitchenName || kitchen?.kitchen_name || kitchen?.name || '';
    const kitchenSlug = createRestaurantSlug(kitchenName) || kid;
    setShowSuggestions(false);
    setNavSearchQuery('');
    if (selectedLocation?.slug) {
      navigate(`/customer/${selectedLocation.slug}/${kitchenSlug}`);
    } else {
      navigate(`/customer/kitchen/${kid}`);
    }
  };

  // When user clicks a location suggestion: set it as selected location and
  // navigate to the search results page showing kitchens in that location
  const handleLocationSearchClick = (place) => {
    const cityName = place.city || place.name || '';
    const slug = cityName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const newLocation = { ...place, name: place.name, city: cityName, slug };
    setSelectedLocation(newLocation);
    setShowSuggestions(false);
    setNavSearchQuery('');
    // Navigate to that location's kitchens listing
    if (slug) {
      navigate(`/customer/${slug}`);
    } else {
      navigate(`/customer/search?q=${encodeURIComponent(place.name)}`);
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleDetectLocation = async () => {
    setLocationLoading(true);
    try {
      const pos = await getCurrentLocation();
      const geocoded = await reverseGeocode(pos.latitude, pos.longitude);
      const locationWithSlug = { ...geocoded, manuallySelected: true };
      setSelectedLocation(locationWithSlug);
      setShowLocationModal(false);
      
      // Navigate to location-based URL
      if (locationWithSlug.slug) {
        navigate(`/customer/${locationWithSlug.slug}`, { replace: true });
      }
    } catch (e) {
      alert('Could not detect location. Please enter manually.');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleLocationSearch = async (q) => {
    setLocationSearch(q);
    if (q.length < 2) { setLocationSuggestions([]); return; }
    const results = await geocodeAddress(q);
    setLocationSuggestions(results);
  };

  const handleSelectSuggestion = (s) => {
    const locationWithSlug = { ...s, manuallySelected: true };
    setSelectedLocation(locationWithSlug);
    setShowLocationModal(false);
    setLocationSearch('');
    setLocationSuggestions([]);
    
    // Navigate to location-based URL
    if (locationWithSlug.slug) {
      navigate(`/customer/${locationWithSlug.slug}`, { replace: true });
    }
  };

  const isActive = (path) => routerLocation.pathname === path || routerLocation.pathname.startsWith(path + '/');

  const locationLabel = selectedLocation?.name || selectedLocation?.city || 'Set Location';
  
  // Helper to build location-aware URLs
  const getLocationPath = (path) => {
    if (selectedLocation?.slug && path !== '/customer') {
      return `/customer/${selectedLocation.slug}${path.replace('/customer', '')}`;
    }
    return path;
  };

  return (
    <nav className="customer-navbar">
      <div className="customer-navbar-inner">
        <Link to={selectedLocation?.slug ? `/customer/${selectedLocation.slug}` : '/customer'} className="customer-navbar-brand">
          <div className="brand-logo-container">
            <span className="powered-by-text">Powered by</span>
            <img src="/biryani-logo.png" alt="Biryani" className="brand-logo-img" />
          </div>
        </Link>

        {/* Location Button - prominent in navbar */}
        <button className="navbar-location-btn" onClick={() => setShowLocationModal(true)}>
          <MapPin size={15} />
          <span className="navbar-location-text">{locationLabel}</span>
          <ChevronDown size={14} />
        </button>

        {/* Navbar Search Bar */}
        <div className="navbar-search-bar" ref={searchRef}>
          <Search size={16} className="navbar-search-icon" />
          <input
            type="text"
            className="navbar-search-input"
            placeholder="Search kitchens or dishes…"
            value={navSearchQuery}
            onChange={(e) => handleNavSearch(e.target.value)}
            onFocus={() => navSearchQuery.length >= 2 && setShowSuggestions(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && navSearchQuery.trim()) {
                setShowSuggestions(false);
                const searchPath = selectedLocation?.slug
                  ? `/customer/${selectedLocation.slug}/search`
                  : '/customer/search';
                navigate(`${searchPath}?q=${encodeURIComponent(navSearchQuery.trim())}`);
              }
              if (e.key === 'Escape') { setShowSuggestions(false); }
            }}
          />
          {navSearchQuery && (
            <button
              className="navbar-search-clear"
              onClick={() => { setNavSearchQuery(''); setKitchenSuggestions([]); setDishSuggestions([]); setLocationSearchSuggestions([]); setShowSuggestions(false); }}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
          
          {/* Smart Suggestions dropdown - Locations + Kitchens + Dishes */}
          {showSuggestions && (locationSearchSuggestions.length > 0 || kitchenSuggestions.length > 0 || dishSuggestions.length > 0) && (
            <div className="navbar-search-suggestions">

              {/* ── Locations ── */}
              {locationSearchSuggestions.length > 0 && (
                <div className="suggestions-section">
                  <div className="suggestions-section-header">Locations</div>
                  {locationSearchSuggestions.map((place, idx) => (
                    <button
                      key={idx}
                      className="suggestion-item"
                      onClick={() => handleLocationSearchClick(place)}
                    >
                      <div className="suggestion-icon suggestion-icon-location">
                        <MapPin size={14} />
                      </div>
                      <div className="suggestion-content">
                        <div className="suggestion-name">{place.name}</div>
                        {place.fullAddress && place.fullAddress !== place.name && (
                          <div className="suggestion-meta">{place.fullAddress.split(',').slice(1, 3).join(',').trim()}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* ── Kitchens ── */}
              {kitchenSuggestions.length > 0 && (
                <div className="suggestions-section">
                  <div className="suggestions-section-header">Kitchens</div>
                  {kitchenSuggestions.map((k) => {
                    const kid = k.kitchen_id || k.id;
                    const name = k.kitchen_name || k.name || 'Kitchen';
                    const address = [k.address_street, k.address_city, expandState(k.address_state)]
                      .filter(Boolean).join(', ');
                    return (
                      <button key={kid} className="suggestion-item" onClick={() => handleKitchenClick(k)}>
                        <div className="suggestion-icon">🏪</div>
                        <div className="suggestion-content">
                          <div className="suggestion-name">{name}</div>
                          {address && <div className="suggestion-meta">{address}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* ── Dishes ── */}
              {dishSuggestions.length > 0 && (
                <div className="suggestions-section">
                  <div className="suggestions-section-header">Dishes</div>
                  {dishSuggestions.map((dish, idx) => {
                    const dishName = dish.item_name || dish.name || 'Dish';
                    const kitchenName = dish.kitchenName || '';
                    const isVeg = dish.is_veg !== false && dish.food_type !== 'non_veg';
                    return (
                      <button
                        key={`${dish.kitchenId}-${dish.item_id || dish.id || idx}`}
                        className="suggestion-item"
                        onClick={() => handleDishClick(dish)}
                      >
                        <div className={`suggestion-icon ${isVeg ? 'veg' : 'non-veg'}`}>
                          <div className="veg-indicator"><div className="veg-dot"></div></div>
                        </div>
                        <div className="suggestion-content">
                          <div className="suggestion-name">{dishName}</div>
                          {kitchenName && <div className="suggestion-meta">at {kitchenName}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`customer-navbar-links ${menuOpen ? 'open' : ''}`}>
          <Link 
            to="/customer"
            className={`nav-link ${routerLocation.pathname === '/customer' ? 'active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            Home
          </Link>
        </div>

        <div className="customer-navbar-actions">
          {/* Cart — only shown when there are items */}
          {cartItemCount > 0 && (
            <button className="nav-cart-btn" onClick={handleCartClick} aria-label={`Cart (${cartItemCount} items)`}>
              <span className="nav-cart-icon-wrap">
                <ShoppingCart size={22} />
                <span className="cart-count-badge">{cartItemCount}</span>
              </span>
            </button>
          )}

          {/* Auth */}
          {isAuthenticated ? (
            <div className="profile-dropdown-wrapper">
              <button className="profile-btn" onClick={() => setProfileOpen(!profileOpen)}>
                <span className="profile-avatar">
                  {customer?.first_name?.[0]?.toUpperCase() || '👤'}
                </span>
                <span className="profile-name">{customer?.first_name || 'Account'}</span>
                <ChevronDown size={14} />
              </button>
              {profileOpen && (
                <div className="profile-dropdown">
                  <Link to="/customer/profile" className="dropdown-item" onClick={() => setProfileOpen(false)}>
                    <User size={16} /> Profile
                  </Link>
                  <Link to="/customer/orders" className="dropdown-item" onClick={() => setProfileOpen(false)}>
                    <Package size={16} /> Orders
                  </Link>
                  <Link to="/customer/addresses" className="dropdown-item" onClick={() => setProfileOpen(false)}>
                    <MapPin size={16} /> Addresses
                  </Link>
                  <Link to="/customer/favorites" className="dropdown-item" onClick={() => setProfileOpen(false)}>
                    <Heart size={16} /> Favorites
                  </Link>
                  <Link to="/customer/support" className="dropdown-item" onClick={() => setProfileOpen(false)}>
                    <HelpCircle size={16} /> Support
                  </Link>
                  <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid var(--border)' }} />
                  <button className="dropdown-item danger" onClick={handleLogout}>
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn-outline-primary" onClick={handleLoginClick}>Login</button>
              <button className="btn-primary" style={{ padding: '8px 16px', fontSize: '14px' }} onClick={handleLoginClick}>Sign Up</button>
            </div>
          )}

          {/* Hamburger */}
          <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Close dropdown on outside click */}
      {profileOpen && (
        <div className="dropdown-overlay" onClick={() => setProfileOpen(false)} />
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="modal-container modal-medium" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Change Location</h3>
              <button className="modal-close" onClick={() => setShowLocationModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <button
                className="detect-location-btn"
                onClick={handleDetectLocation}
                disabled={locationLoading}
              >
                <MapPin size={18} />
                {locationLoading ? 'Detecting…' : 'Use My Current Location'}
              </button>
              <div className="location-divider"><span>or search</span></div>
              <input
                type="text"
                className="form-input"
                placeholder="Search city or area…"
                value={locationSearch}
                onChange={(e) => handleLocationSearch(e.target.value)}
                autoFocus
              />
              {locationSuggestions.length > 0 && (
                <div className="location-suggestions" style={{ marginTop: 8 }}>
                  {locationSuggestions.map((s, i) => (
                    <button key={i} className="location-suggestion-item" onClick={() => handleSelectSuggestion(s)}>
                      <MapPin size={15} /> {s.name}
                      <small>{s.fullAddress}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </nav>
  );
};

export default Navbar;
